import React from "react";
import { useLocation } from "react-router-dom";
import { Box } from "@chakra-ui/react";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarInset, SidebarProvider, useCloseMobileSidebar } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/app-sidebar";
import { ShadcnSidebarInsetHeader } from "@/components/shadcn-sidebar-inset-header";
import { ShadcnInsetChromeContext } from "@/contexts/ShadcnInsetChromeContext";
import { ShadcnInsetBreadcrumbProvider } from "@/contexts/ShadcnInsetBreadcrumbContext";
import { DocumentTitleSync } from "@/components/DocumentTitleSync";
import { ShadcnInsetHeaderActionsProvider } from "@/contexts/ShadcnInsetHeaderActionsContext";
import { NotificationsInboxProvider } from "@/contexts/NotificationsInboxContext";
import {
  isNotificationsCentrePathname,
  NOTIFICATIONS_CENTRE_SIDEBAR_WIDTH,
} from "@/constants/notificationsCentre";
import { isWorkspaceCanvasPathname } from "@/views/Dashboard/Workspace/workspaceRoutes";
import { cn } from "@/lib/utils";

/** Closes the mobile sheet after in-app navigation (skip initial mount). */
function SidebarMobileAutoClose() {
  const { pathname } = useLocation();
  const closeMobileSidebar = useCloseMobileSidebar();
  const isInitialPath = React.useRef(true);

  React.useEffect(() => {
    if (isInitialPath.current) {
      isInitialPath.current = false;
      return;
    }
    closeMobileSidebar();
  }, [pathname, closeMobileSidebar]);

  return null;
}

export interface DashboardShellProps {
  sidebarOpen: boolean;
  onSidebarOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

/**
 * shadcn sidebar-07 shell for all viewports (sheet drawer below md, rail + inset at md+).
 */
export function DashboardShell({
  sidebarOpen,
  onSidebarOpenChange,
  children,
}: DashboardShellProps) {
  const { pathname } = useLocation();
  const notificationsCentre = isNotificationsCentrePathname(pathname);
  const workspaceCanvas = isWorkspaceCanvasPathname(pathname);

  return (
    <Box w="100%" minH="100vh">
      <ShadcnInsetBreadcrumbProvider>
        <DocumentTitleSync />
        <ShadcnInsetHeaderActionsProvider>
        <SidebarProvider
          collapsible="icon"
          open={sidebarOpen}
          onOpenChange={onSidebarOpenChange}
          style={
            notificationsCentre
              ? ({
                  "--sidebar-width": NOTIFICATIONS_CENTRE_SIDEBAR_WIDTH,
                } as React.CSSProperties)
              : undefined
          }
        >
          <SidebarMobileAutoClose />
          <NotificationsInboxProvider enabled={notificationsCentre}>
          <TooltipProvider delayDuration={0}>
            <AppSidebar />
            <SidebarInset
              className={cn(
                "flex min-h-svh min-w-0 flex-1 flex-col bg-content-canvas",
                workspaceCanvas &&
                  "h-svh min-h-0 overflow-hidden md:!m-0 md:peer-data-[variant=inset]:!m-0 md:peer-data-[variant=inset]:!ml-0 md:peer-data-[variant=inset]:!rounded-none"
              )}
            >
              <ShadcnInsetChromeContext.Provider value={true}>
                <ShadcnSidebarInsetHeader />
                <div
                  className={
                    notificationsCentre
                      ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-x-hidden overflow-y-auto bg-content-canvas p-4"
                      : workspaceCanvas
                        ? "flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-content-canvas p-0"
                        : "flex min-h-0 min-w-0 flex-1 flex-col gap-4 overflow-x-hidden bg-content-canvas p-4"
                  }
                >
                  {children}
                </div>
              </ShadcnInsetChromeContext.Provider>
            </SidebarInset>
          </TooltipProvider>
          </NotificationsInboxProvider>
        </SidebarProvider>
        </ShadcnInsetHeaderActionsProvider>
      </ShadcnInsetBreadcrumbProvider>
    </Box>
  );
}
